import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { hasDefault, resolveProfileFields } from '@/lib/profileFields'
import { FieldValueInput } from './FieldValueInput'
import { useConditionOptionLabel } from './storage/ConditionControls'
import { FieldRulesEditor } from './FieldRulesEditor'
import { DATA_TYPE_LABELS } from '@/lib/fieldValidation'
import { PROFILE_TYPE_LABEL, STORAGE_CONDITION_FIELD } from '@/lib/inventoryProfiles'
import {
  useDeleteFieldDef,
  useFieldDefs,
  useSaveFieldDef,
  useSaveProfile,
} from '@/hooks/useInventoryMaster'
import { usePermission } from '@/lib/permissions'
import { storageTemperaturesHref } from '@/lib/storageRoutes'
import type {
  ApiFieldDef,
  ApiProfile,
  FieldDataType,
  FieldValidation,
  InventoryProfileType,
  ProfileFieldEntry,
} from '@/types/inventory'

const DATA_TYPES: FieldDataType[] = ['string', 'number', 'boolean', 'date', 'enum', 'file']

/** Derive a stable camelCase machine key from a human label ("Barcode Required" → "barcodeRequired"). */
function toKey(label: string): string {
  return label
    .trim()
    .replace(/[^a-zA-Z0-9 ]/g, '')
    .split(/\s+/)
    .filter(Boolean)
    .map((w, i) => (i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join('')
}

interface ProfileFieldsModalProps {
  profileType: InventoryProfileType
  /** null = create a new profile; an ApiProfile = edit that profile. */
  profile: ApiProfile | null
  open: boolean
  onClose: () => void
  onSaved?: (id: string) => void
}

/**
 * The Storage Condition field on a storage profile. Its options are the
 * tenant's storage conditions, which put-away compares against, so they are
 * edited on the Temperatures screen (and kept in step by the server), not
 * typed in here. Its label and description are the tenant's; it cannot be removed.
 */
function isConditionField(profileType: InventoryProfileType, key: string) {
  return profileType === 'storage' && key === STORAGE_CONDITION_FIELD
}

/**
 * Edit an existing field in place: its label, description, rules and — for a
 * select — its options. The key and type stay: profiles and materials already
 * store values under that key, in that type.
 */
function FieldDefEditor({
  def,
  locked,
  onDone,
  onEditConditions,
}: {
  def: ApiFieldDef
  /** The Storage Condition field: its choices are the tenant's conditions, edited in their own dialog. */
  locked: boolean
  onDone: () => void
  onEditConditions: () => void
}) {
  const saveFieldDef = useSaveFieldDef()
  const [label, setLabel] = useState(def.label)
  const [description, setDescription] = useState(def.description ?? '')
  const [rules, setRules] = useState<FieldValidation>(def.validation ?? {})
  const [options, setOptions] = useState<string[]>(def.options ?? [])
  const [error, setError] = useState<string | null>(null)

  function save() {
    if (!label.trim()) {
      setError('A field needs a label.')
      return
    }
    if (def.dataType === 'enum' && !locked && options.length === 0) {
      setError('A select field needs at least one option.')
      return
    }
    saveFieldDef.mutate(
      {
        id: def.id,
        payload: {
          label: label.trim(),
          description: description.trim(),
          // `{}` clears every rule; the server treats it as "no rules".
          validation: rules,
          ...(def.dataType === 'enum' && !locked ? { options } : {}),
        },
      },
      {
        onSuccess: onDone,
        onError: (err: unknown) => setError(err instanceof Error ? err.message : 'Could not save the field.'),
      },
    )
  }

  return (
    <div className='mt-2 space-y-2 rounded-lg border border-blue-100 bg-blue-50/40 p-2.5'>
      <div className='flex flex-wrap items-center gap-2'>
        <Input
          value={label}
          onChange={(e) => {
            setLabel(e.target.value)
            setError(null)
          }}
          placeholder='Field label'
          aria-label='Field label'
          className='h-8 w-40 bg-white'
        />
        <Input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder='Description (optional)'
          aria-label='Field description'
          className='h-8 min-w-0 flex-1 bg-white'
        />
      </div>

      {locked ? (
        <div className='flex flex-wrap items-center gap-2 text-xs text-gray-500'>
          <span>The choices are your storage conditions (Refrigerated 2-8 C, Freezer -20 C, …).</span>
          <Button size='sm' variant='outline' className='h-7 bg-white text-xs' onClick={onEditConditions}>
            Edit storage conditions
          </Button>
        </div>
      ) : (
        <FieldRulesEditor
          dataType={def.dataType}
          validation={rules}
          onValidationChange={(v) => {
            setRules(v)
            setError(null)
          }}
          options={options}
          onOptionsChange={(o) => {
            setOptions(o)
            setError(null)
          }}
        />
      )}

      {error && <p className='text-xs text-destructive'>{error}</p>}

      <div className='flex items-center gap-2'>
        <Button size='sm' onClick={save} disabled={saveFieldDef.isPending}>
          {saveFieldDef.isPending ? 'Saving…' : 'Save field'}
        </Button>
        <Button size='sm' variant='ghost' onClick={onDone}>
          Cancel
        </Button>
      </div>
    </div>
  )
}

/** Create or edit a profile: name it + switch each predefined field on/off (+ add more), and edit the fields themselves. */
export function ProfileFieldsModal({ profileType, profile, open, onClose, onSaved }: ProfileFieldsModalProps) {
  const { can } = usePermission()
  const canConfigure = can('inventory.configure')
  const { data: defs = [] } = useFieldDefs(profileType)
  const saveProfile = useSaveProfile()
  const saveFieldDef = useSaveFieldDef()
  const deleteFieldDef = useDeleteFieldDef()

  const [name, setName] = useState('')
  const [draft, setDraft] = useState<Record<string, ProfileFieldEntry>>({})
  const [addingField, setAddingField] = useState(false)
  const [newLabel, setNewLabel] = useState('')
  const [newDesc, setNewDesc] = useState('')
  const [newType, setNewType] = useState<FieldDataType>('string')
  const [newRules, setNewRules] = useState<FieldValidation>({})
  const [newOptions, setNewOptions] = useState<string[]>([])
  const [addError, setAddError] = useState<string | null>(null)
  /** The field def open for editing, by key. One at a time keeps the list readable. */
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const navigate = useNavigate()
  // Asked before "Edit conditions" leaves with unsaved profile edits.
  const [confirmLeave, setConfirmLeave] = useState(false)
  // Unsaved edits to the profile itself: its name, or a field switched or given
  // a default. Field definitions save on their own, so they never count.
  const dirty = name !== (profile?.name ?? '') || Object.keys(draft).length > 0
  // The Temperatures screen, not a dialog stacked on this one. Leaving drops
  // the draft, so with one it asks first — in the app, not `window.confirm`.
  const goToTemperatures = () => navigate(storageTemperaturesHref())
  const editConditions = () => (dirty ? setConfirmLeave(true) : goToTemperatures())

  const descByKey = useMemo(() => new Map(defs.map((d) => [d.key, d.description])), [defs])
  const conditionLabel = useConditionOptionLabel()

  useEffect(() => {
    setName(profile?.name ?? '')
    setDraft({})
    setAddingField(false)
    setEditingKey(null)
    setConfirmLeave(false)
  }, [profile, open])

  const resolved = useMemo(
    () => resolveProfileFields(defs, { ...(profile?.fields ?? {}), ...draft }),
    [defs, profile, draft],
  )
  const yesCount = resolved.filter((f) => f.active).length
  const defaultCount = resolved.filter((f) => f.active && hasDefault(f)).length

  function setField(key: string, patch: Partial<ProfileFieldEntry>) {
    setDraft((prev) => {
      const base = prev[key] ?? profile?.fields?.[key] ?? { active: false, value: undefined }
      return { ...prev, [key]: { ...base, ...patch } }
    })
  }

  function resetNewField() {
    setNewLabel('')
    setNewDesc('')
    setNewType('string')
    setNewRules({})
    setNewOptions([])
    setAddError(null)
  }

  /** Catch the rule mistakes the backend would reject, before spending a request. */
  function ruleProblem(): string | null {
    if (newRules.min !== undefined && newRules.max !== undefined && newRules.min > newRules.max) {
      return 'Min must be less than or equal to Max.'
    }
    if (newRules.minDate && newRules.maxDate && Date.parse(newRules.minDate) > Date.parse(newRules.maxDate)) {
      return 'Earliest must be on or before Latest.'
    }
    if (newRules.pattern) {
      try {
        new RegExp(newRules.pattern)
      } catch {
        return 'Pattern is not a valid regular expression.'
      }
    }
    if (newType === 'enum' && newOptions.length === 0) {
      return 'A select field needs at least one option.'
    }
    return null
  }

  function addField() {
    const key = toKey(newLabel)
    if (!newLabel.trim() || !key) return
    const problem = ruleProblem()
    if (problem) {
      setAddError(problem)
      return
    }
    const validation = Object.keys(newRules).length > 0 ? newRules : undefined
    saveFieldDef.mutate(
      {
        payload: {
          profileType,
          key,
          label: newLabel.trim(),
          description: newDesc.trim() || undefined,
          dataType: newType,
          order: defs.length,
          options: newType === 'enum' ? newOptions : undefined,
          validation,
        },
      },
      {
        onSuccess: () => {
          resetNewField()
          setAddingField(false)
        },
        // The backend is the authority on rule shape — show what it rejected
        // instead of silently closing the row.
        onError: (err: unknown) => setAddError(err instanceof Error ? err.message : 'Could not add the field.'),
      },
    )
  }

  function save() {
    if (!name.trim()) return
    const fields: Record<string, ProfileFieldEntry> = Object.fromEntries(
      resolved.map((f) => [f.key, { active: f.active, value: f.value }]),
    )
    if (profile) {
      saveProfile.mutate(
        { id: profile.id, payload: { name: name.trim(), fields } },
        { onSuccess: () => { onSaved?.(profile.id); onClose() } },
      )
    } else {
      saveProfile.mutate(
        { payload: { profileType, name: name.trim(), fields } },
        { onSuccess: (p) => { onSaved?.(p.id); onClose() } },
      )
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader className='space-y-1'>
          <DialogTitle className='text-lg'>
            {profile ? 'Edit profile' : 'New profile'} · {PROFILE_TYPE_LABEL[profileType]}
          </DialogTitle>
        </DialogHeader>

        <div className='flex items-center gap-2'>
          <Input
            autoFocus={!profile}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder='Profile name'
            className='h-9 flex-1'
            disabled={!canConfigure}
          />
          <span className='shrink-0 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-600'>
            {yesCount} field{yesCount === 1 ? '' : 's'} YES
          </span>
          {/* Which fields apply is one thing; what they hand down is another,
              and only the second reaches a material. */}
          {defaultCount > 0 && (
            <span className='shrink-0 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700'>
              {defaultCount} with a default
            </span>
          )}
        </div>

        <div className='max-h-[48vh] overflow-y-auto rounded-xl border border-gray-200'>
          <ul>
            {resolved.map((f) => {
              const def = defs.find((d) => d.key === f.key)
              // Booleans say everything with the switch itself; anything else
              // needs somewhere to put the value the profile hands down.
              const takesDefault = f.active && f.dataType !== 'boolean' && def
              const locked = isConditionField(profileType, f.key)
              const editing = editingKey === f.key && def
              return (
                <li key={f.key} className='border-b border-gray-50 px-4 py-2.5 last:border-0'>
                  <div className='flex items-center justify-between gap-3'>
                    <div className='min-w-0'>
                      <div className='text-sm text-gray-700'>{f.label}</div>
                      {descByKey.get(f.key) && (
                        <div className='text-xs text-gray-400'>{descByKey.get(f.key)}</div>
                      )}
                    </div>
                    <div className='flex shrink-0 items-center gap-2'>
                      <Switch
                        checked={f.active}
                        disabled={!canConfigure}
                        onCheckedChange={(v) =>
                          setField(f.key, f.dataType === 'boolean' ? { active: v, value: v } : { active: v })
                        }
                      />
                      {canConfigure && def && (
                        <button
                          type='button'
                          className={editingKey === f.key ? 'text-primary' : 'text-gray-300 hover:text-primary'}
                          title='Edit field'
                          aria-label={`Edit field ${f.label}`}
                          onClick={() => setEditingKey(editingKey === f.key ? null : f.key)}
                        >
                          <Pencil className='h-4 w-4' />
                        </button>
                      )}
                      {/* Built in: every storage profile needs it, and the server refuses the delete. */}
                      {canConfigure && !locked && (
                        <button
                          type='button'
                          className='text-gray-300 hover:text-red-500'
                          title='Remove field'
                          onClick={() => {
                            const defId = def?.id
                            if (defId && window.confirm(`Remove field "${f.label}"? Affects all profiles of this type.`))
                              deleteFieldDef.mutate(defId)
                          }}
                        >
                          <Trash2 className='h-4 w-4' />
                        </button>
                      )}
                    </div>
                  </div>

                  {editing && (
                    <FieldDefEditor
                      key={def.id}
                      def={def}
                      locked={locked}
                      onDone={() => setEditingKey(null)}
                      onEditConditions={editConditions}
                    />
                  )}

                  {/* The value every material on this profile inherits unless it
                      states its own. Without it a profile could only say WHICH
                      fields apply, never what they are — so "Storage Profile:
                      Cryo" carried no condition and put-away had nothing to
                      check against. */}
                  {takesDefault && (
                    <div className='mt-2 flex flex-wrap items-center gap-2'>
                      <span className='text-[11px] uppercase tracking-wide text-gray-400'>
                        Default
                      </span>
                      {canConfigure ? (
                        <FieldValueInput
                          def={def}
                          value={f.value}
                          onChange={(v) => setField(f.key, { active: true, value: v })}
                          optionLabel={conditionLabel}
                        />
                      ) : (
                        <span className='text-sm text-gray-700'>
                          {f.value == null || f.value === ''
                            ? '—'
                            : conditionLabel(String(f.value))}
                        </span>
                      )}
                      {locked && canConfigure && (
                        <button
                          type='button'
                          className='text-xs font-medium text-primary hover:underline'
                          onClick={editConditions}
                        >
                          Edit conditions
                        </button>
                      )}
                    </div>
                  )}
                </li>
              )
            })}
          </ul>

          {canConfigure && (
            <div className='border-t border-gray-100 bg-gray-50/50 px-4 py-3'>
              {addingField ? (
                <div className='space-y-2'>
                  <div className='flex flex-wrap items-end gap-2'>
                    <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder='Field label' className='h-8 w-40' />
                    <Input value={newDesc} onChange={(e) => setNewDesc(e.target.value)} placeholder='Description (optional)' className='h-8 w-48' />
                    <Select
                      value={newType}
                      onValueChange={(v) => {
                        // Rules are per-type; carrying them across a type change
                        // would send keys the backend rejects for the new type.
                        setNewType(v as FieldDataType)
                        setNewRules({})
                        setNewOptions([])
                        setAddError(null)
                      }}
                    >
                      <SelectTrigger className='h-8 w-28'>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DATA_TYPES.map((t) => (
                          <SelectItem key={t} value={t}>
                            {DATA_TYPE_LABELS[t]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <FieldRulesEditor
                    dataType={newType}
                    validation={newRules}
                    onValidationChange={(v) => {
                      setNewRules(v)
                      setAddError(null)
                    }}
                    options={newOptions}
                    onOptionsChange={(o) => {
                      setNewOptions(o)
                      setAddError(null)
                    }}
                  />

                  {addError && <p className='text-xs text-destructive'>{addError}</p>}

                  <div className='flex items-center gap-2'>
                    <Button size='sm' onClick={addField} disabled={saveFieldDef.isPending}>
                      {saveFieldDef.isPending ? 'Adding…' : 'Add'}
                    </Button>
                    <Button
                      size='sm'
                      variant='ghost'
                      onClick={() => {
                        resetNewField()
                        setAddingField(false)
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  type='button'
                  className='flex items-center gap-1.5 text-sm font-medium text-primary hover:underline'
                  onClick={() => setAddingField(true)}
                >
                  <Plus className='h-4 w-4' />
                  Add more fields
                </button>
              )}
            </div>
          )}
        </div>

        <p className='text-xs text-gray-400'>
          Switch each field on to include it in this profile. Add more fields to extend the list.
        </p>

        <div className='flex justify-end gap-2'>
          <Button variant='outline' onClick={onClose}>
            Close
          </Button>
          {canConfigure && (
            <Button onClick={save} disabled={!name.trim() || saveProfile.isPending}>
              {saveProfile.isPending ? 'Saving…' : 'Save profile'}
            </Button>
          )}
        </div>
      </DialogContent>

      <Dialog open={confirmLeave} onOpenChange={(o) => !o && setConfirmLeave(false)}>
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>Discard unsaved changes?</DialogTitle>
            <DialogDescription>
              Your changes to this profile are not saved. Going to Temperatures discards them.
            </DialogDescription>
          </DialogHeader>
          <div className='flex justify-end gap-2'>
            <Button variant='outline' onClick={() => setConfirmLeave(false)}>
              Stay
            </Button>
            <Button
              variant='destructive'
              onClick={() => {
                setConfirmLeave(false)
                goToTemperatures()
              }}
            >
              Discard &amp; go
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  )
}
